import i18n from '@/utils/i18n';
import Negative from '@assets/icons/negative.svg';
import Neutral from '@assets/icons/neutral.svg';
import Positive from '@assets/icons/positive.svg';
import React, { useRef } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { SampleRequestFeedbackType } from '@/types';
import { TextArea } from '@/components/text-area';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';

interface Props {
  feedbackText: string;
  feedbackType?: SampleRequestFeedbackType;
  onFeedbackTextChange: (feedbackText: string) => void;
  onFeedbackTypeSelect: (feedbackType: SampleRequestFeedbackType) => void;
  onSubmit: () => void;
  sampleRequestUUID: string;
}

export const SampleRequestFollowUpFeedback: React.FC<Props> = ({
  feedbackText,
  feedbackType,
  onFeedbackTextChange,
  onFeedbackTypeSelect,
  onSubmit,
  sampleRequestUUID,
}) => {
  const [styles] = useStyles();
  const inputRef = useRef<TextInput>(null);
  const onFollowUpFeedbackTypeSelect = (
    feedbackType: SampleRequestFeedbackType,
  ) => {
    onFeedbackTypeSelect(feedbackType);
    inputRef.current?.focus();
  };

  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();

  return (
    <>
      <View style={styles.feedbackType}>
        <TouchableOpacity
          onPress={() => {
            onFollowUpFeedbackTypeSelect(SampleRequestFeedbackType.Negative);
          }}
          style={
            feedbackType === SampleRequestFeedbackType.Negative
              ? null
              : styles.unselectedFeedbackType
          }
          testID={`feedback_icon_${SampleRequestFeedbackType.Negative}`}
        >
          <Negative />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            onFollowUpFeedbackTypeSelect(SampleRequestFeedbackType.Neutral);
          }}
          style={
            feedbackType === SampleRequestFeedbackType.Neutral
              ? null
              : styles.unselectedFeedbackType
          }
          testID={`feedback_icon_${SampleRequestFeedbackType.Neutral}`}
        >
          <Neutral />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            onFollowUpFeedbackTypeSelect(SampleRequestFeedbackType.Positive);
          }}
          style={
            feedbackType === SampleRequestFeedbackType.Positive
              ? null
              : styles.unselectedFeedbackType
          }
          testID={`feedback_icon_${SampleRequestFeedbackType.Positive}`}
        >
          <Positive />
        </TouchableOpacity>
      </View>
      <View style={styles.followUpFeedback}>
        <TextArea
          containerStyle={styles.textAreaContainer}
          inputRef={inputRef}
          maxLength={500}
          onChangeText={onFeedbackTextChange}
          placeholder={i18n.t('additional_feedback_to_share')}
          value={feedbackText}
        />
      </View>
      <Button
        disabled={!feedbackType}
        onPress={onSubmit}
        trackingData={{
          name: 'sample_request_follow_up.feedback_submitted',
          properties: {
            feedbackType,
            restaurantUUID,
            sampleRequestUUID,
            supplierUUID,
          },
        }}
      >
        {i18n.t('submit')}
      </Button>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  feedbackType: {
    flexDirection: 'row',
    gap: 64,
    justifyContent: 'center',
    marginBottom: 32,
  },
  followUpFeedback: {
    marginBottom: 16,
  },
  textAreaContainer: {
    borderColor: theme.colors.primary,
  },
  unselectedFeedbackType: {
    opacity: 0.4,
  },
}));
